/**
 * Pitch zones and channels: the frontend's one copy of
 * app/utils/football/zones.py and channels.py, tied to them through
 * lib/testdata/pitch_zones.json (lib/pitchZones.test.ts and
 * tests/test_pitch_zones_frontend.py).
 *
 *   DEF  defensive third               x < -L/6
 *   MFD  middle third, own half        -L/6 <= x < 0
 *   MFA  middle third, opponent half   0 <= x < L/6
 *   ATT  attacking third               x >= L/6
 *
 * x is metres from halfway, positive toward the goal the team attacks; each
 * zone holds its lower edge. L is the game's zone length in metres, 105 m
 * when it has none. A missing x or length gives no zone.
 *
 *   WRC  wide right     y < -20.16
 *   IRC  inside right   -20.16 <= y < -9.16
 *   CC   central        -9.16 <= y < 9.16
 *   ILC  inside left    9.16 <= y < 20.16
 *   WLC  wide left      y >= 20.16
 *
 * y is metres from the pitch's long axis in the attacking team's view,
 * negative on the attacker's right; each channel holds its lower edge. The
 * lines are the goal area's edges and the penalty area's, the same on every
 * pitch. A missing y gives no channel, and so does a pitch 42 m wide or
 * less. Each caller turns its y into the attacking view first: a team
 * attacking -x sees y with its sign turned, as it sees x.
 */

/** The length a game with no real pitch size is cut on, metres. */
export const DEFAULT_LENGTH_M = 105

export const CODES = ['DEF', 'MFD', 'MFA', 'ATT'] as const
export type ZoneCode = typeof CODES[number]

/** Each line between two zones, in sixths of the length from halfway. */
export const LINES_IN_SIXTHS = [-1, 0, 1] as const

/** The match page's zone filter values. */
export const PAGE_ZONES: Record<ZoneCode, string> = {
  DEF: 'defensive', MFD: 'middle-def', MFA: 'middle-att', ATT: 'attacking',
}
/** The match page's zone names, as the moment card and filter show them. */
export const ZONE_NAMES: Record<ZoneCode, string> = {
  DEF: 'Defensive', MFD: 'Middle-DEF', MFA: 'Middle-ATT', ATT: 'Attacking',
}
/** The zone labels the backend stores (zones.SHORT_LABELS). */
export const SHORT_LABELS = {
  DEF: 'defensive', MFD: 'mid-def', MFA: 'mid-att', ATT: 'attacking',
} as const

const missing = (v: number | null | undefined): v is null | undefined =>
  v == null || Number.isNaN(v)

/** The three lines between the zones, metres from halfway: -L/6, 0, L/6. */
export function edgesM(lengthM: number): [number, number, number] {
  const [low, mid, high] = LINES_IN_SIXTHS.map((k) => (k * lengthM) / 6)
  return [low!, mid!, high!]
}

/** The three lines, metres from the goal line defended. */
export function edgesFromGoalLineM(lengthM: number): [number, number, number] {
  const [low, mid, high] = edgesM(lengthM).map((e) => lengthM / 2 + e)
  return [low!, mid!, high!]
}

/** Each zone's span, metres from the goal line the team defends. */
export function spansFromGoalLineM(lengthM: number): Record<ZoneCode, [number, number]> {
  const [low, mid, high] = edgesFromGoalLineM(lengthM)
  return { DEF: [0, low], MFD: [low, mid], MFA: [mid, high], ATT: [high, lengthM] }
}

export function zoneCode(
  xAttM: number | null | undefined, lengthM: number | null | undefined,
): ZoneCode | null {
  if (missing(xAttM) || missing(lengthM)) return null
  return zoneCodeAt(xAttM, edgesM(lengthM))
}

/** The zone code for x against `lines`, the three lines between the zones
 *  in ascending order on x's own scale (edgesM for a true x from halfway;
 *  the server's zone_lines_m for a served canonical x from the own goal
 *  line). Each zone holds its lower edge; a missing x gives no zone. */
export function zoneCodeAt(
  x: number | null | undefined, lines: readonly [number, number, number],
): ZoneCode | null {
  if (missing(x)) return null
  const [low, mid, high] = lines
  if (x < low) return 'DEF'
  if (x < mid) return 'MFD'
  if (x < high) return 'MFA'
  return 'ATT'
}

/** The goal's width, metres. Both box widths are measured out from its
 *  posts, so the two lines are derived from it once. */
export const GOAL_WIDTH_M = 7.32
/** The goal area's edge, metres from the long axis (9.16). */
export const GOAL_AREA_HALF_WIDTH_M = GOAL_WIDTH_M / 2 + 5.5
/** The penalty area's edge, metres from the long axis (20.16). */
export const PEN_AREA_HALF_WIDTH_M = GOAL_WIDTH_M / 2 + 16.5

/** The width a game with no real pitch size is read as, metres. */
export const DEFAULT_WIDTH_M = 68
/** A pitch this wide or narrower gets no channels. */
export const MIN_WIDTH_M = 42

/** The channel codes, from the attacker's right to the attacker's left. */
export const CHANNEL_CODES = ['WRC', 'IRC', 'CC', 'ILC', 'WLC'] as const
export type ChannelCode = typeof CHANNEL_CODES[number]

/** The match page's channel filter values. */
export const PAGE_CHANNELS: Record<ChannelCode, string> = {
  WRC: 'wide-right', IRC: 'inside-right', CC: 'central',
  ILC: 'inside-left', WLC: 'wide-left',
}
/** The match page's channel names, as the filter shows them. */
export const CHANNEL_NAMES: Record<ChannelCode, string> = {
  WRC: 'Wide Right', IRC: 'Inside Right', CC: 'Central',
  ILC: 'Inside Left', WLC: 'Wide Left',
}
/** The possession flow lanes (channels.FLOW_LANE_LABELS). */
export const FLOW_LANE_LABELS = {
  WRC: 'right_wing', IRC: 'right_half', CC: 'centre',
  ILC: 'left_half', WLC: 'left_wing',
} as const
/** Wings only: the two wide channels, and everything inside the penalty
 *  area's width as central (channels.WING_LABELS). */
export const WING_LABELS = {
  WRC: 'right wing', IRC: 'central', CC: 'central',
  ILC: 'central', WLC: 'left wing',
} as const
/** Three sides: each side is its wide and inside channel, the centre the
 *  goal area's width (channels.COPILOT_SIDE_LABELS). */
export const SIDE_LABELS = {
  WRC: 'right', IRC: 'right', CC: 'centre', ILC: 'left', WLC: 'left',
} as const

/** The four lines between the channels, metres from the long axis, from
 *  the attacker's right. */
export function channelEdgesM(): [number, number, number, number] {
  return [-PEN_AREA_HALF_WIDTH_M, -GOAL_AREA_HALF_WIDTH_M,
    GOAL_AREA_HALF_WIDTH_M, PEN_AREA_HALF_WIDTH_M]
}

/** Whether a pitch widthM metres wide gets no channels. */
export function channelsRefused(widthM: number | null | undefined): boolean {
  return missing(widthM) || widthM <= MIN_WIDTH_M
}

/** The channel code of yAttM, metres in the attacking team's view, on a
 *  pitch widthM metres wide; null with no y or a refused width. centreM is
 *  yAttM's value on the long axis: 34 for a y from the attacker's right
 *  touchline on the 68 m frame. Each line is then centreM plus an edge,
 *  which lands on 13.84 .. 54.16 exactly, where taking 34 from y would not
 *  (43.16 - 34 < 9.16). */
export function channelCode(
  yAttM: number | null | undefined, widthM: number | null | undefined,
  centreM = 0,
): ChannelCode | null {
  if (missing(yAttM) || channelsRefused(widthM)) return null
  const edges = channelEdgesM()
  for (let i = 0; i < edges.length; i++) {
    if (yAttM < centreM + edges[i]!) return CHANNEL_CODES[i]!
  }
  return 'WLC'
}
